/* AXOLOVE: "One / Pair" tiles plus a colour row for a product whose single
 * option mixes both (Knee Booster: "One — gray", "One — black",
 * "Pair — both knees (gray)", "Pair — both knees (black)").
 *
 * The block carries a JSON map from "units|colour" to the theme's option value
 * to pick. Picking it in the theme's own (hidden) picker keeps price, SKU and
 * gallery with the theme. A combination with `items` has no SKU of its own:
 * the add is taken over to put the listed lines in the cart instead.
 *
 * The colour row uses the theme's variant button classes and the same
 * data-current-checked / data-previous-checked marks as variant-picker.js, so
 * the highlight slides like the theme's own buttons.
 *
 * "Two pairs" (units 4) is for two people: the single colour row gives way to
 * two rows, one per pair, and the add puts one pair SKU per row in the cart (a
 * "from 2" discount on the pair SKUs brings any mix to the two-pairs price).
 * The theme keeps showing the single's price.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const root = document.querySelector('[data-ax-set-colour]');

if (root) {
  const config = JSON.parse(root.querySelector('script[type="application/json"]').textContent);
  const optionName = config.option;
  let units = 1;
  let colour = config.colours[0].key;

  const productForm = () => document.querySelector('product-form-component');
  const themeOption = (value) =>
    document.querySelector(`variant-picker input[data-option-name="${optionName}"][value="${CSS.escape(value)}"]`);
  const combo = () => config.combos[`${Math.min(units, 2)}|${colour}`];
  const pairsRow = root.querySelector('[data-ax-colour-pairs]');

  function items() {
    if (units !== 4) return combo()?.items;
    const counts = new Map();
    pairsRow.querySelectorAll('input:checked').forEach((input) => {
      const id = config.pair[input.value];
      counts.set(id, (counts.get(id) || 0) + 1);
    });
    return Array.from(counts, ([id, quantity]) => ({ id, quantity }));
  }

  // The colour row borrows the classes of the theme's picker so it gets the same
  // colours (the picker block may carry its own custom-colour class).
  const row = root.querySelector('[data-ax-colour-row]');
  const picker = document.querySelector('variant-picker');
  if (row && picker) row.className = `${picker.className} ax-set-colour__row`;
  if (pairsRow && picker) pairsRow.className = `${picker.className} ax-set-colour__row`;

  function slidePill(input) {
    const fieldset = input.closest('fieldset');
    if (!fieldset) return;
    const radios = Array.from(fieldset.querySelectorAll('input'));
    const previous = radios.find((radio) => radio !== input && radio.dataset.currentChecked === 'true');
    radios.forEach((radio) => {
      radio.dataset.previousChecked = 'false';
      radio.dataset.currentChecked = 'false';
    });
    input.dataset.currentChecked = 'true';
    if (previous) previous.dataset.previousChecked = 'true';
    fieldset.style.setProperty('--pill-width-current', `${input.parentElement.offsetWidth}px`);
    if (previous) fieldset.style.setProperty('--pill-width-previous', `${previous.parentElement.offsetWidth}px`);
  }

  function apply() {
    if (row) row.hidden = units === 4;
    if (pairsRow) pairsRow.hidden = units !== 4;
    const target = units === 4 ? config.combos['1|gray'] : combo();
    if (!target) return;
    const option = themeOption(target.value);
    if (option && !option.checked) option.click();
  }

  // Start from whatever the theme has selected (a shared link may carry a variant).
  const checked = document.querySelector(`variant-picker input[data-option-name="${optionName}"]:checked`);
  if (checked) {
    const entry = Object.entries(config.combos).find(([, c]) => c.value === checked.value && !c.items);
    if (entry) [units, colour] = [Number(entry[0].split('|')[0]), entry[0].split('|')[1]];
  }
  root.querySelectorAll('input[name="ax-pack"]').forEach((input) => {
    input.checked = Number(input.value) === units;
  });
  root.querySelectorAll('input[name="ax-colour"]').forEach((input) => {
    input.checked = input.value === colour;
    input.dataset.currentChecked = String(input.checked);
    input.dataset.previousChecked = 'false';
  });

  root.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name === 'ax-pack') units = Number(target.value) || 1;
    if (target.name.startsWith('ax-colour-')) {
      slidePill(target);
      return;
    }
    if (target.name === 'ax-colour') {
      colour = target.value;
      slidePill(target);
    }
    apply();
  });

  async function addItems(component, items) {
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    component.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((r) => r.json());
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      const didError = Boolean(response.status);
      if (didError) component.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'product-form-component',
          sourceId: component.id?.toString(),
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          productId: component.dataset.productId,
          sections: didError ? undefined : response.sections,
          didError,
        },
      });
      return !didError;
    } catch (error) {
      deferred.reject(error);
      component.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      return false;
    }
  }

  // The theme delegates `submit` from `document` in the capture phase; `window`
  // is reached first.
  window.addEventListener(
    'submit',
    (event) => {
      const list = items();
      const component = event.target instanceof Element ? event.target.closest('product-form-component') : null;
      if (!list || !component) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      addItems(component, list);
    },
    true
  );

  window.addEventListener(
    'click',
    async (event) => {
      const list = items();
      const target = event.target;
      if (!list || !(target instanceof Element) || !target.closest('.shopify-payment-button, shopify-buy-it-now-button, shopify-accelerated-checkout')) return;
      const component = productForm();
      if (!component) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (await addItems(component, list)) window.location.href = '/checkout';
    },
    true
  );
}
